import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { LOGO_URL, NAV_LINKS } from "@/lib/site-config";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/70 backdrop-blur-2xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="flex items-center gap-3 group"
        >
          <img
            src={LOGO_URL}
            alt="EchoWings Media"
            className="h-9 md:h-10 w-auto transition-transform duration-500 group-hover:scale-105"
          />
        </Link>

        <ul className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) => {
            const active = location.pathname === link.href;
            return (
              <li key={link.href}>
                <Link
                  to={link.href}
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                  className={`relative px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                    active
                      ? "text-cyan-400"
                      : "text-white/70 hover:text-white"
                  }`}
                >
                  {link.label}
                  {active && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute left-3 right-3 -bottom-1 h-px bg-gradient-to-r from-blue-500 to-cyan-400"
                    />
                  )}
                </Link>
              </li>
            );
          })}
        </ul>

        <div className="hidden lg:block">
          <Link
            to="/book-audit"
            data-testid="nav-cta-book-audit"
            className="group inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-400 text-black font-semibold text-sm px-5 py-2.5 rounded-full hover:shadow-[0_0_30px_rgba(0,229,255,0.45)] transition-all duration-300"
          >
            Book Free Audit
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/5 border border-white/10 text-white"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-black/95 backdrop-blur-2xl border-t border-white/10"
            data-testid="mobile-menu"
          >
            <ul className="px-6 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                    className="block px-3 py-3 text-base text-white/80 hover:text-cyan-400 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li className="pt-3">
                <Link
                  to="/book-audit"
                  data-testid="mobile-nav-cta"
                  onClick={() => setOpen(false)}
                  className="block text-center bg-gradient-to-r from-blue-600 to-cyan-400 text-black font-semibold text-sm px-5 py-3 rounded-full"
                >
                  Book Free Audit
                </Link>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
